import {
  useMutation,
  useQuery,
  useQueryClient,
} from "@tanstack/react-query";

import { api } from "./client";

// Must match engine_v2.SUPPORTED_RULE_TYPES (the canonical engine) and the
// backend's RuleType Literal. Previously offered camera_degraded / site_unhealthy
// (creatable but NEVER evaluated by v2) and motion_detected / stream_error (no
// evaluator in EITHER engine), while hiding recording_gap / recording_stopped /
// health_threshold, which work.
//
// SS15: camera_degraded is BACK — it now has a real engine_v2 evaluator, so it
// fires. site_unhealthy is DROPPED — not merely unported: its evaluator and the
// whole v1 engine.py were deleted. It stays creatable via the API (C-soft, like
// motion_detected / stream_error) and reports engine_supported=false, so the
// "never fires" badge shows on the rules list. It returns as a SCOPE OPTION on
// health_threshold, not as a rule type — backlog §15a.
export type RuleType =
  | "camera_offline"
  | "camera_degraded"
  | "recording_gap"
  | "recording_stopped"
  | "health_threshold"
  | "license_expiring"
  | "agent_unreachable";

export type Severity = "info" | "warning" | "critical";
export type EventStatus = "open" | "acknowledged" | "resolved";

export interface AlertRule {
  id: string;
  tenant_id: string;
  site_id?: string | null;
  camera_id?: string | null;
  name: string;
  rule_type: RuleType;
  condition_params: Record<string, unknown>;
  severity: Severity;
  enabled: boolean;
  notify_email: boolean;
  notify_webhook: boolean;
  webhook_url?: string | null;
  cooldown_minutes: number;
  created_by?: string | null;
  created_at: string;
  updated_at: string;
  /** Server-computed: true iff engine_v2 has an evaluator for this rule_type.
   *  FALSE means the rule is stored and "enabled" but will NEVER fire — a
   *  soft-accepted ("C-soft") type. Must be surfaced, or a dead rule looks
   *  active. */
  engine_supported?: boolean;
}

export interface AlertEvent {
  id: string;
  tenant_id: string;
  rule_id?: string | null;
  camera_id?: string | null;
  site_id?: string | null;
  // S17A — widened to string: SInsight alerts use rule_type="sinsight".
  rule_type: RuleType | string;
  alert_type?: string | null;
  severity: Severity;
  title: string;
  message: string;
  status: EventStatus;
  acknowledged_by?: string | null;
  acknowledged_at?: string | null;
  resolved_by?: string | null;
  resolved_at?: string | null;
  extra_metadata: Record<string, unknown>;
  created_at: string;
  // S17A — alarm verification + escalation surface.
  runbook_id?: string | null;
  auto_clip_id?: string | null;
  escalation_level?: number;
  escalated_at?: string | null;
}

export interface AlertEventList {
  items: AlertEvent[];
  total: number;
  limit: number;
  offset: number;
}

export interface AlertEventSummary {
  open: Record<Severity, number>;
  acknowledged: number;
  resolved_24h: number;
  total_open: number;
}

export interface AlertRuleList {
  items: AlertRule[];
  total: number;
}

export interface AlertRuleInput {
  name: string;
  rule_type: RuleType;
  site_id?: string | null;
  camera_id?: string | null;
  condition_params?: Record<string, unknown>;
  severity?: Severity;
  enabled?: boolean;
  notify_email?: boolean;
  notify_webhook?: boolean;
  webhook_url?: string | null;
  cooldown_minutes?: number;
}

const alertsKey = ["alerts"] as const;

// ---------- Rules ----------
export function useAlertRules() {
  return useQuery({
    queryKey: [...alertsKey, "rules"],
    queryFn: async () => {
      const { data } = await api.get<AlertRuleList>("/api/v1/alerts/rules");
      return data;
    },
  });
}

export function useCreateRule() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: async (input: AlertRuleInput) => {
      const { data } = await api.post<AlertRule>(
        "/api/v1/alerts/rules",
        input,
      );
      return data;
    },
    onSuccess: () => qc.invalidateQueries({ queryKey: alertsKey }),
  });
}

export function useUpdateRule(id: string) {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: async (patch: Partial<AlertRuleInput>) => {
      const { data } = await api.patch<AlertRule>(
        `/api/v1/alerts/rules/${id}`,
        patch,
      );
      return data;
    },
    onSuccess: () => qc.invalidateQueries({ queryKey: alertsKey }),
  });
}

export function useToggleRule() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: async (id: string) => {
      const { data } = await api.patch<AlertRule>(
        `/api/v1/alerts/rules/${id}/toggle`,
      );
      return data;
    },
    onSuccess: () => qc.invalidateQueries({ queryKey: alertsKey }),
  });
}

export function useDeleteRule() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: async (id: string) => {
      await api.delete(`/api/v1/alerts/rules/${id}`);
    },
    onSuccess: () => qc.invalidateQueries({ queryKey: alertsKey }),
  });
}

// ---------- Events ----------
export interface AlertEventFilters {
  status?: EventStatus;
  severity?: Severity;
  rule_type?: RuleType;
  site_id?: string;
  camera_id?: string;
  from?: string;
  to?: string;
  limit?: number;
  offset?: number;
}

export function useAlertEvents(filters: AlertEventFilters = {}) {
  return useQuery({
    queryKey: [...alertsKey, "events", filters],
    queryFn: async () => {
      const params = new URLSearchParams();
      if (filters.status) params.set("status", filters.status);
      if (filters.severity) params.set("severity", filters.severity);
      if (filters.rule_type) params.set("rule_type", filters.rule_type);
      if (filters.site_id) params.set("site_id", filters.site_id);
      if (filters.camera_id) params.set("camera_id", filters.camera_id);
      if (filters.from) params.set("from", filters.from);
      if (filters.to) params.set("to", filters.to);
      params.set("limit", String(filters.limit ?? 50));
      params.set("offset", String(filters.offset ?? 0));
      const { data } = await api.get<AlertEventList>(
        `/api/v1/alerts/events?${params.toString()}`,
      );
      return data;
    },
    refetchInterval: 30_000,
  });
}

export function useAlertEventsSummary() {
  return useQuery({
    queryKey: [...alertsKey, "summary"],
    queryFn: async () => {
      const { data } = await api.get<AlertEventSummary>(
        "/api/v1/alerts/events/summary",
      );
      return data;
    },
    refetchInterval: 30_000,
  });
}

export function useAcknowledgeEvent() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: async (id: string) => {
      const { data } = await api.post<AlertEvent>(
        `/api/v1/alerts/events/${id}/acknowledge`,
      );
      return data;
    },
    onSuccess: () => qc.invalidateQueries({ queryKey: alertsKey }),
  });
}

export function useResolveEvent() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: async (id: string) => {
      const { data } = await api.post<AlertEvent>(
        `/api/v1/alerts/events/${id}/resolve`,
      );
      return data;
    },
    onSuccess: () => qc.invalidateQueries({ queryKey: alertsKey }),
  });
}

/** D1 — BULK acknowledge / resolve.
 *
 * Sequential, not parallel, and per-row rather than one transactional call:
 *
 *   * There is no bulk endpoint, and inventing one is a backend + authz +
 *     audit-shape decision (backlog §68), not a UI decision.
 *   * `AuditService.log` is per-resource. Firing N requests keeps N audit rows,
 *     which is the granularity that makes the log worth having. A single bulk
 *     call would need a deliberate answer to "one audit row or N?" first.
 *   * Sequential so a partial failure has a defined boundary: everything before
 *     the failure applied, everything after did not, and the caller is told
 *     exactly which. Parallel would leave an arbitrary subset applied.
 *
 * Returns the ids that succeeded and the first error, so the caller can report
 * "7 of 9 acknowledged" instead of a bare success or a bare failure.
 */
export interface BulkResult {
  succeeded: string[];
  failed: { id: string; error: unknown }[];
}

async function bulkPost(ids: string[], path: (id: string) => string): Promise<BulkResult> {
  const out: BulkResult = { succeeded: [], failed: [] };
  for (const id of ids) {
    try {
      await api.post(path(id));
      out.succeeded.push(id);
    } catch (error) {
      out.failed.push({ id, error });
    }
  }
  return out;
}

export function useBulkAcknowledgeEvents() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (ids: string[]) =>
      bulkPost(ids, (id) => `/api/v1/alerts/events/${id}/acknowledge`),
    onSettled: () => qc.invalidateQueries({ queryKey: alertsKey }),
  });
}

export function useBulkResolveEvents() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (ids: string[]) =>
      bulkPost(ids, (id) => `/api/v1/alerts/events/${id}/resolve`),
    onSettled: () => qc.invalidateQueries({ queryKey: alertsKey }),
  });
}

// ---------- S17A — alarm verification ----------
export interface RunbookContact {
  name: string;
  phone: string;
  role: string;
}

export interface Runbook {
  id: string;
  tenant_id: string;
  alert_type: string;
  title: string;
  steps: string[];
  contacts: RunbookContact[];
  created_at: string;
  updated_at: string;
}

export interface DetectionEventSummary {
  detection_event_id?: string | null;
  detection_type?: string | null;
  confidence?: number | null;
  bounding_box?: Record<string, number> | null;
  snapshot_ref?: string | null;
  occurred_at?: string | null;
}

export interface AlarmVerification {
  alert: AlertEvent;
  signed_stream_url?: string | null;
  latest_snapshot_url?: string | null;
  runbook?: Runbook | null;
  detection_event?: DetectionEventSummary | null;
}

export function useAlarmVerification(alertId: string | null) {
  return useQuery({
    queryKey: [...alertsKey, "verification", alertId],
    enabled: !!alertId,
    queryFn: async () => {
      const { data } = await api.get<AlarmVerification>(
        `/api/v1/alerts/${alertId}/verification`,
      );
      return data;
    },
    // Stream tokens are 60s; don't cache stale URLs across opens.
    staleTime: 0,
    refetchOnMount: "always",
  });
}

// S17B — manual escalation (unified path).
export function useEscalateEvent() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: async (id: string) => {
      const { data } = await api.post<AlertEvent>(
        `/api/v1/alerts/events/${id}/escalate`,
      );
      return data;
    },
    onSuccess: () => qc.invalidateQueries({ queryKey: alertsKey }),
  });
}

// S10 — suppress event until a future ISO datetime.
export function useSuppressEvent() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: async (input: { id: string; suppress_until: string }) => {
      const { data } = await api.post<AlertEvent>(
        `/api/v1/alerts/events/${input.id}/suppress`,
        { suppress_until: input.suppress_until },
      );
      return data;
    },
    onSuccess: () => qc.invalidateQueries({ queryKey: alertsKey }),
  });
}
